import { isPopupUnsubscribe } from '../utils/env';
import { log } from '../utils/debug';
import { getPianoSegmentsHash } from '../utils/piano';
import {
  INT_RADIX, TRUE_STRING, SUBSCRIPTION_STATUS, KEY_SUBSCRIPTION_STATUS, KEY_DENY_COUNT, KEY_UNREAD_NOTIFICATIONS_COUNT, ZERO_STRING, KEY_SPEECHBUBBLE_UNREAD_NOTIFICATIONS, KEY_HAS_UNREAD_NOTIFICATIONS
} from '../const/common';

const KEY_DENY_SESSIONS = 'cleverpush-deny-sessions';
const KEY_DENY_TIME = 'cleverpush-deny-time';
const KEY_DENY_SESSIONS_COUNTED = 'cleverpush-deny-sessions-counted';
const KEY_CLOSE_TIME = 'cleverpush-close-time';
const KEY_CLOSE_SESSIONS = 'cleverpush-close-sessions';
const KEY_CLOSE_SESSIONS_COUNTED = 'cleverpush-close-sessions-counted';
const KEY_SUBSCRIPTION_ID = 'cleverpush-subscription-id';
const KEY_SUBSCRIPTION_ID_OLD = 'push-subscription-id';
const KEY_SUBSCRIPTION_STATUS_OLD = 'push-subscription-status';
const KEY_DOMAIN = 'cleverpush-domain';
const DEFAULT_DOMAIN = 'cleverpush.com';
const KEY_LAST_SYNC = 'cleverpush-last-sync';
const KEY_LAST_SESSION = 'cleverpush-last-session';
const KEY_VISITS = 'cleverpush-visits';
const KEY_ATTRIBUTES = 'cleverpush-attributes';
const KEY_ATTRIBUTE_PREFIX = 'cleverpush-attribute';
const KEY_TAG_PREFIX = 'cleverpush-tag';
const KEY_TAGS = 'cleverpush-tags';
const KEY_TAG_DATES = 'cleverpush-tag-dates';
const KEY_TOPICS = 'cleverpush-topics';
const KEY_TOPICS_VERSION = 'cleverpush-topics-version';
const KEY_LAST_WORKER_UPDATE = 'cleverpush-last-worker-update';
const KEY_LAST_WORKER_VERSION = 'cleverpush-last-worker-version';
const KEY_NOTIFICATION_CLICKED = 'cleverpush-notification-clicked';
const KEY_FACEBOOK_USER_REF = 'cleverpush-facebook-user-ref';
const KEY_AMP_WEB_PUSH_PERMISSION = 'amp-web-push-notification-permission';
const KEY_PIANO_SEGMENTS_HASH = 'cleverpush-piano-segments-hash';
const KEY_READ_NOTIFICATIONS = 'cleverpush-read-notifications';
const AMP_WEB_PUSH_PERMISSION_GRANTED = 'granted';
const INDEXED_DB_DATABASE = 'cleverpush';
const INDEXED_DB_TABLE_NOTIFICATIONS = 'notifications';
const INDEXED_DB_TABLE_SUBSCRIPTION = 'subscription';
const INDEXED_DB_VERSION = 40;
const INDEXED_DB_NOTIFICATIONS_MAXIMUM = 50;
const INDEXED_DB_ERROR_VERSION = 'VersionError';
const INDEXED_DB_ERROR_DATABASE = 'NoSuchDatabaseError';
const INDEXED_DB_CURSOR_PREV = 'prev';
const INDEXED_DB_TRANSACTION_READONLY = 'readonly';
const INDEXED_DB_TRANSACTION_READWRITE = 'readwrite';
const ALERT_HOURS_DENY_DEFAULT = 365 * 24;
const MILLIS_PER_HOUR = 60 * 60 * 1000;
const SYNC_INTERVAL_HOURS = 2 * 24;
const SYNC_INTERVAL_HOURS_PIANO = 2;
const UPDATE_WORKER_INTERVAL_24_HOURS = 24;

export default class StorageManager {
  constructor(config) {
    this.config = config;

    try {
      if ('localStorage' in window) {
        // set cleverpush domain to default if there is a subscription
        if (config.cleverpushDomain) {
          if (
            config.cleverpushDomain
            && localStorage.getItem(KEY_SUBSCRIPTION_ID)
            && !localStorage.getItem(KEY_DOMAIN)
          ) {
            localStorage.setItem(KEY_DOMAIN, DEFAULT_DOMAIN);
          }
          if (localStorage.getItem(KEY_DOMAIN)) {
            this.config.cleverpushDomain = localStorage.getItem(KEY_DOMAIN);
          }
        }

        // transfer old values
        if (
          localStorage.getItem(KEY_SUBSCRIPTION_STATUS_OLD)
          && !localStorage.getItem(KEY_SUBSCRIPTION_STATUS)
        ) {
          localStorage.setItem(KEY_SUBSCRIPTION_STATUS, localStorage.getItem(KEY_SUBSCRIPTION_STATUS_OLD));
        }
        if (
          localStorage.getItem(KEY_SUBSCRIPTION_ID_OLD)
          && !localStorage.getItem(KEY_SUBSCRIPTION_ID)
        ) {
          localStorage.setItem(KEY_SUBSCRIPTION_ID, localStorage.getItem(KEY_SUBSCRIPTION_ID_OLD));
        }

        // check frequency capping
        if (
          localStorage.getItem(KEY_SUBSCRIPTION_STATUS) !== SUBSCRIPTION_STATUS.ALLOWED
        ) {
          const currentTime = Date.now();
          const denyCount = parseInt(localStorage.getItem(KEY_DENY_COUNT), INT_RADIX);
          const isUnsubscribed = localStorage.getItem(KEY_SUBSCRIPTION_STATUS) === SUBSCRIPTION_STATUS.UNSUBSCRIBED;

          let alertHoursDeny = isUnsubscribed ? this.config.alertHoursUnsubscribe : this.config.alertHoursDeny;
          const alertSessionsDeny = isUnsubscribed ? this.config.alertSessionsUnsubscribe : this.config.alertSessionsDeny;

          const alertHoursSecondDeny = isUnsubscribed ? this.config.alertHoursUnsubscribe : this.config.alertHoursSecondDeny;
          const alertSessionsSecondDeny = isUnsubscribed ? this.config.alertSessionsUnsubscribe : this.config.alertSessionsSecondDeny;

          if (!isUnsubscribed && typeof alertHoursDeny === 'undefined') {
            // default: 365 days
            alertHoursDeny = ALERT_HOURS_DENY_DEFAULT;
          }

          let denyTimeAllowed = false;
          let denySessionsAllowed = false;

          if (localStorage.getItem(KEY_DENY_TIME) && alertHoursDeny > 0) {
            const denyTime = parseInt(localStorage.getItem(KEY_DENY_TIME), INT_RADIX);
            const denyHours = parseInt(alertHoursDeny, INT_RADIX);
            if (!isNaN(denyTime) && !isNaN(denyHours) && !isNaN(denyCount) && denyCount <= 1) {
              if (denyTime + (denyHours * MILLIS_PER_HOUR) < currentTime) {
                denyTimeAllowed = true;
              }
            }
          }

          // second deny hours
          if (localStorage.getItem(KEY_DENY_TIME) && alertHoursSecondDeny > 0) {
            const denyTime = parseInt(localStorage.getItem(KEY_DENY_TIME), INT_RADIX);
            const denyHours = parseInt(alertHoursDeny, INT_RADIX);
            if (!isNaN(denyTime) && !isNaN(denyHours) && !isNaN(denyCount) && denyCount > 1) {
              if (denyTime + (denyHours * MILLIS_PER_HOUR) < currentTime) {
                denyTimeAllowed = true;
              }
            }
          }

          if (localStorage.getItem(KEY_DENY_SESSIONS) && alertSessionsDeny > 0) {
            let denySessions = parseInt(localStorage.getItem(KEY_DENY_SESSIONS), INT_RADIX);
            if (!isNaN(denySessions) && denyCount <= 1) {
              if (!sessionStorage.getItem(KEY_DENY_SESSIONS_COUNTED)) {
                denySessions += 1;
                localStorage.setItem(KEY_DENY_SESSIONS, denySessions);
              }
              if (denySessions >= alertSessionsDeny) {
                denySessionsAllowed = true;
              }
            }
          }

          // second session deny
          if (localStorage.getItem(KEY_DENY_SESSIONS) && alertSessionsSecondDeny > 0) {
            let denySessions = parseInt(localStorage.getItem(KEY_DENY_SESSIONS), INT_RADIX);
            if (!isNaN(denySessions) && !isNaN(denyCount) && denyCount > 1) {
              if (!sessionStorage.getItem(KEY_DENY_SESSIONS_COUNTED)) {
                denySessions += 1;
                localStorage.setItem(KEY_DENY_SESSIONS, denySessions);
              }
              if (denySessions >= alertSessionsDeny) {
                denySessionsAllowed = true;
              }
            }
          }

          if (
            (denyTimeAllowed || denySessionsAllowed)
            && (!alertHoursDeny || denyTimeAllowed)
            && (!alertSessionsDeny || denySessionsAllowed)
          ) {
            this.setDenyStatus();
          }

          // second deny
          if (
            (denyTimeAllowed || denySessionsAllowed)
            && (!alertHoursSecondDeny || denyTimeAllowed)
            && (!alertSessionsSecondDeny || denySessionsAllowed)
          ) {
            this.setDenyStatus();
          }

          if (!isUnsubscribed) {
            let closeTimeAllowed = false;
            let closeSessionsAllowed = false;

            if (localStorage.getItem(KEY_CLOSE_TIME) && this.config.alertHoursClose > 0) {
              const closeTime = parseInt(localStorage.getItem(KEY_CLOSE_TIME), INT_RADIX);
              const closeHours = parseInt(this.config.alertHoursClose, INT_RADIX);
              if (!isNaN(closeTime) && !isNaN(closeHours)) {
                if (closeTime + (closeHours * MILLIS_PER_HOUR) < currentTime) {
                  closeTimeAllowed = true;
                }
              }
            }

            if (localStorage.getItem(KEY_CLOSE_SESSIONS) && this.config.alertSessionsClose > 0) {
              let closeSessions = parseInt(localStorage.getItem(KEY_CLOSE_SESSIONS), INT_RADIX);
              if (!isNaN(closeSessions)) {
                if (!sessionStorage.getItem(KEY_CLOSE_SESSIONS_COUNTED)) {
                  closeSessions += 1;
                  localStorage.setItem(KEY_CLOSE_SESSIONS, closeSessions);
                }
                if (closeSessions >= this.config.alertSessionsClose) {
                  closeSessionsAllowed = true;
                }
              }
            }

            if (
              (closeTimeAllowed || closeSessionsAllowed)
              && (!this.config.alertHoursClose || closeTimeAllowed)
              && (!this.config.alertSessionsClose || closeSessionsAllowed)
            ) {
              localStorage.removeItem(KEY_SUBSCRIPTION_STATUS_OLD);
              localStorage.removeItem(KEY_SUBSCRIPTION_STATUS);
              localStorage.removeItem(KEY_CLOSE_TIME);
              localStorage.removeItem(KEY_CLOSE_SESSIONS);
            }
          }
        }
      }
    } catch (error) {
      log.warn('Failed getting managing storage (frequency capping)', error);
    }
  }

  setDenyStatus() {
    localStorage.removeItem(KEY_SUBSCRIPTION_STATUS_OLD);
    localStorage.removeItem(KEY_DENY_TIME);
    localStorage.removeItem(KEY_DENY_SESSIONS);
    if (
      localStorage.getItem(KEY_SUBSCRIPTION_STATUS) === SUBSCRIPTION_STATUS.DENIED
      || localStorage.getItem(KEY_SUBSCRIPTION_STATUS) === SUBSCRIPTION_STATUS.UNSUBSCRIBED
    ) {
      localStorage.setItem(KEY_SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS.WAS_DENIED);
    } else {
      localStorage.removeItem(KEY_SUBSCRIPTION_STATUS);
    }
  }

  dbOpenError(error) {
    this.dbInitializing = false;
    if (error.message && error.message.toString().includes(INDEXED_DB_ERROR_VERSION)) {
      log.info('deleting db');

      const deleteReq = window.indexedDB.deleteDatabase(INDEXED_DB_DATABASE);
      deleteReq.onsuccess = () => {
        const openReq = this.db.open();
        openReq.onsuccess = () => this.dbOpenSuccess(openReq);
        openReq.onerror = (openError) => {
          log.warn(openError);
        };
      };
    } else if (!error.name || error.name !== INDEXED_DB_ERROR_DATABASE) {
      log.warn(error);
    }
  }

  dbOpenSuccess(openReq) {
    this.db = openReq.result;
    this.dbInitializing = false;

    this.db.onerror = (error) => {
      log.warn('IndexedDB error occured', error);
    };

    this.db.onversionchange = (error) => {
      if (error.newVersion === null) {
        error.target.close();
      } else {
        this.db.close();
        const versionDbOpen = window.indexedDB.open(INDEXED_DB_DATABASE, INDEXED_DB_VERSION);

        versionDbOpen.onsuccess = () => this.dbOpenSuccess(versionDbOpen);
        versionDbOpen.onerror = this.dbOpenError;
      }
    };
  }

  initDb(callback) {
    if (!this.db && window.indexedDB && !this.dbInitializing) {
      this.dbInitializing = true;
      try {
        const openReq = window.indexedDB.open(INDEXED_DB_DATABASE, INDEXED_DB_VERSION);

        openReq.onsuccess = () => {
          this.dbOpenSuccess(openReq);
          if (typeof callback === 'function') {
            callback(openReq.result);
          }
        };
        openReq.onerror = this.dbOpenError;
        openReq.onupgradeneeded = (event) => {
          const db = event.target.result;

          const notifications = db.createObjectStore(INDEXED_DB_TABLE_NOTIFICATIONS, { keyPath: 'id' });
          notifications.createIndex('id', 'id', { unique: true });
          notifications.createIndex('url', 'url', { unique: false });
          notifications.createIndex('clicked', 'clicked', { unique: false });
          notifications.createIndex('title', 'title', { unique: false });
          notifications.createIndex('text', 'text', { unique: false });
          notifications.createIndex('icon', 'icon', { unique: false });
          notifications.createIndex('subscriptionId', 'subscriptionId', { unique: false });
          notifications.createIndex('channelId', 'channelId', { unique: false });
          notifications.createIndex('autoHide', 'autoHide', { unique: false });
          notifications.createIndex('markedAsDelivered', 'markedAsDelivered', { unique: false });
          notifications.createIndex('markedAsClicked', 'markedAsClicked', { unique: false });
          notifications.createIndex('clickedAction', 'clickedAction', { unique: false });
          notifications.createIndex('deliveredAt', 'deliveredAt', { unique: false });

          const subscription = db.createObjectStore(INDEXED_DB_TABLE_SUBSCRIPTION, { keyPath: 'id' });
          subscription.createIndex('id', 'id', { unique: true });
          subscription.createIndex('channelId', 'channelId', { unique: false });
        };
      } catch (error) {
        this.dbInitializing = false;
        log.info('initDb error', error);
      }
    } else if (this.db) {
      if (typeof callback === 'function') {
        callback(this.db);
      }
    }
  }

  getNotifications(max) {
    return new Promise((resolve) => {
      const notifications = [];

      if (this.db) {
        const transaction = this.db.transaction([INDEXED_DB_TABLE_NOTIFICATIONS], INDEXED_DB_TRANSACTION_READONLY);
        const notificationsObjectStore = transaction.objectStore(INDEXED_DB_TABLE_NOTIFICATIONS);
        const deliveredAtIndex = notificationsObjectStore.index('deliveredAt');

        const cursorRequest = deliveredAtIndex.openCursor(null, INDEXED_DB_CURSOR_PREV);

        cursorRequest.onsuccess = (event) => {
          const result = event.target.result;
          if (!!result === false || notifications.length >= (max || INDEXED_DB_NOTIFICATIONS_MAXIMUM)) {
            resolve(notifications);
            return;
          }

          notifications.push(result.value);

          result.continue();
        };
        cursorRequest.onerror = () => resolve(notifications);
      } else {
        resolve(notifications);
      }
    });
  }

  getNotification(id) {
    return new Promise((resolve) => {
      if (this.db) {
        const transaction = this.db.transaction([INDEXED_DB_TABLE_NOTIFICATIONS]);
        const notificationsObjectStore = transaction.objectStore(INDEXED_DB_TABLE_NOTIFICATIONS);

        const getRequest = notificationsObjectStore.get(id);
        getRequest.onsuccess = (event) => {
          const notification = event.target.result;
          resolve(notification);
        };
        getRequest.onerror = () => resolve(null);
      } else {
        resolve(null);
      }
    });
  }

  addNotification(notificationDocument) {
    return new Promise((resolve, reject) => {
      if (this.db) {
        const transaction = this.db.transaction([INDEXED_DB_TABLE_NOTIFICATIONS], INDEXED_DB_TRANSACTION_READWRITE);
        const notificationsObjectStore = transaction.objectStore(INDEXED_DB_TABLE_NOTIFICATIONS);

        const putRequest = notificationsObjectStore.put(notificationDocument);
        putRequest.onsuccess = resolve;
        putRequest.onerror = reject;
      } else {
        resolve(null);
      }
    });
  }

  deleteNotification(id) {
    return new Promise((resolve, reject) => {
      if (this.db) {
        const transaction = this.db.transaction([INDEXED_DB_TABLE_NOTIFICATIONS], INDEXED_DB_TRANSACTION_READWRITE);
        const notificationsObjectStore = transaction.objectStore(INDEXED_DB_TABLE_NOTIFICATIONS);

        const deleteRequest = notificationsObjectStore.delete(id);
        deleteRequest.onsuccess = resolve;
        deleteRequest.onerror = reject;
      } else {
        resolve(null);
      }
    });
  }

  // returns false if the user CAN subscribe (and is not subscribed)
  canSubscribe() {
    try {
      return new Promise((resolve) => {
        try {
          const status = localStorage.getItem(KEY_SUBSCRIPTION_STATUS);
          const sessionStatus = sessionStorage.getItem(KEY_SUBSCRIPTION_STATUS);
          if (
            status !== SUBSCRIPTION_STATUS.DENIED
            && status !== SUBSCRIPTION_STATUS.UNSUBSCRIBED
            && status !== SUBSCRIPTION_STATUS.PENDING
            && sessionStatus !== SUBSCRIPTION_STATUS.DENIED
          ) {
            resolve(status || true);
          } else {
            resolve(false);
          }
        } catch (error) {
          resolve(true);
        }
      });
    } catch (error) {
      return Promise.resolve(true);
    }
  }

  // returns true if user is subscribed
  isSubscribed(allowEmptyId, noPromise, fromIframe) {
    try {
      if (typeof noPromise !== 'undefined' && noPromise) {
        const status = localStorage.getItem(KEY_SUBSCRIPTION_STATUS);
        if (
          status
          && status !== SUBSCRIPTION_STATUS.DENIED
          && status !== SUBSCRIPTION_STATUS.UNSUBSCRIBED
          && status !== SUBSCRIPTION_STATUS.PENDING
        ) {
          if (allowEmptyId) {
            return true;
          }
          const subscriptionIdExists = !!this.getSubscriptionId();
          return subscriptionIdExists;
        }
        return false;
      }

      return new Promise((resolve) => {
        try {
          const status = localStorage.getItem(KEY_SUBSCRIPTION_STATUS);
          if (
            status
            && status !== SUBSCRIPTION_STATUS.DENIED
            && status !== SUBSCRIPTION_STATUS.UNSUBSCRIBED
            && status !== SUBSCRIPTION_STATUS.PENDING
          ) {
            const ampSubscribed = localStorage.getItem(KEY_AMP_WEB_PUSH_PERMISSION) === AMP_WEB_PUSH_PERMISSION_GRANTED;
            if (allowEmptyId || ampSubscribed) {
              resolve(true);
            } else {
              const subscriptionIdExists = !!this.getSubscriptionId();
              resolve(subscriptionIdExists);
            }
          } else if (typeof fromIframe !== 'undefined' && fromIframe) {
            const subscriptionIdExists = !!this.getSubscriptionId();
            resolve(subscriptionIdExists);
          } else {
            resolve(false);
          }
        } catch (error) {
          resolve(false);
        }
      });
    } catch (error) {
      return Promise.resolve(true);
    }
  }

  getSubscriptionId() {
    let id;
    try {
      id = localStorage.getItem(KEY_SUBSCRIPTION_ID);
      if (!id) {
        id = sessionStorage.getItem(KEY_SUBSCRIPTION_ID);
      }
    } catch (error) {
      log.warn('Failed getting subscription ID from storage', error);
    }
    if (!id && isPopupUnsubscribe()) {
      const params = new URLSearchParams(location.search.slice(1));
      if (params) {
        id = params.get('subscriptionId');
      }
    }
    return id;
  }

  setClickedNotification(id) {
    try {
      localStorage.setItem(KEY_NOTIFICATION_CLICKED, JSON.stringify({ id, date: Date.now() }));
    } catch (error) {
      log.info('failed saving notification to storage', error);
    }
  }

  getClickedNotification() {
    try {
      const clickedNotif = localStorage.getItem(KEY_NOTIFICATION_CLICKED);
      const clickedNotifObj = JSON.parse(clickedNotif);
      if (
        clickedNotifObj
        && clickedNotifObj.id
        && clickedNotifObj.date
        && (parseInt(clickedNotifObj.date, INT_RADIX) + MILLIS_PER_HOUR) > Date.now()
      ) {
        return clickedNotifObj.id;
      }
    } catch (error) {
      log.info('failed getting notification from storage', error);
    }
    return null;
  }

  getLastSession() {
    let lastSessionStr;
    try {
      lastSessionStr = localStorage.getItem(KEY_LAST_SESSION);
    } catch (error) {
      log.info('failed getting sessions from storage', error);
    }
    let lastSession = {};
    if (lastSessionStr) {
      try {
        lastSession = JSON.parse(lastSessionStr);
      } catch (error) {
        log.info('failed parsing last session from storage', error);
      }
    }
    return lastSession;
  }

  setLastSession(lastSession) {
    try {
      localStorage.setItem(KEY_LAST_SESSION, JSON.stringify(lastSession));
    } catch (error) {
      log.info('failed saving last session to storage', error);
    }
  }

  setSubscribed(subscriptionId, fromIframe) {
    if (subscriptionId) {
      localStorage.setItem(KEY_SUBSCRIPTION_ID, subscriptionId);

      localStorage.setItem(KEY_DOMAIN, this.config.cleverpushDomain || DEFAULT_DOMAIN);

      if (typeof fromIframe === 'undefined' || !fromIframe) {
        localStorage.setItem(KEY_SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS.ALLOWED);
      } else {
        localStorage.removeItem(KEY_SUBSCRIPTION_STATUS);
      }

      this.initDb((db) => {
        if (db) {
          try {
            const transaction = db.transaction([INDEXED_DB_TABLE_SUBSCRIPTION], INDEXED_DB_TRANSACTION_READWRITE);
            const notificationsObjectStore = transaction.objectStore(INDEXED_DB_TABLE_SUBSCRIPTION);

            const clearRequest = notificationsObjectStore.clear();
            clearRequest.onsuccess = () => {
              const putTransaction = db.transaction([INDEXED_DB_TABLE_SUBSCRIPTION], INDEXED_DB_TRANSACTION_READWRITE);
              const putStore = putTransaction.objectStore(INDEXED_DB_TABLE_SUBSCRIPTION);
              putStore.put({ id: subscriptionId, channelId: this.config.channelId });
            };
          } catch (error) {
            log.info('setSubscribed db error', error);
          }
        }
      });
    }
  }

  getFacebookUserRef() {
    try {
      return localStorage.getItem(KEY_FACEBOOK_USER_REF);
    } catch (error) {
      log.info('failed getting facebook user ref', error);
    }
    return undefined;
  }

  setFacebookUserRef(userRef) {
    if (userRef) {
      localStorage.setItem(KEY_FACEBOOK_USER_REF, userRef);
    }
  }

  setAllowed() {
    localStorage.setItem(KEY_SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS.ALLOWED);
    localStorage.removeItem(KEY_DENY_COUNT);
  }

  setPending() {
    localStorage.setItem(KEY_SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS.PENDING);
  }

  setDenied() {
    localStorage.setItem(KEY_DENY_TIME, Date.now());
    localStorage.setItem(KEY_SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS.DENIED);
    if (this.config.alertSessionsDeny > 0 || this.config.alertSessionsSecondDeny > 0) {
      localStorage.setItem(KEY_DENY_SESSIONS, ZERO_STRING);
      sessionStorage.setItem(KEY_DENY_SESSIONS_COUNTED, TRUE_STRING);
    }

    const hasDenyCountFilter = this.config.confirmAlertFilters?.find((filter) => filter.type === 'denyCountGreaterThan');
    if (this.config.alertHoursSecondDeny || this.config.alertSessionsSecondDeny || hasDenyCountFilter) {
      let denyCount = parseInt(localStorage.getItem(KEY_DENY_COUNT), INT_RADIX);
      if (denyCount && !isNaN(denyCount)) {
        denyCount += 1;
        localStorage.setItem(KEY_DENY_COUNT, denyCount);
      } else {
        localStorage.setItem(KEY_DENY_COUNT, 1);
      }
    }
  }

  setClosed() {
    localStorage.setItem(KEY_CLOSE_TIME, Date.now());

    if (this.config.alertHoursClose > 0 || this.config.alertSessionsClose > 0) {
      localStorage.setItem(KEY_SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS.DENIED);
    } else {
      localStorage.removeItem(KEY_SUBSCRIPTION_STATUS);
    }

    if (this.config.alertSessionsClose > 0) {
      localStorage.setItem(KEY_CLOSE_SESSIONS, ZERO_STRING);
      sessionStorage.setItem(KEY_CLOSE_SESSIONS_COUNTED, TRUE_STRING);
    }
  }

  setUnsubscribed(notManually) {
    localStorage.removeItem(KEY_SUBSCRIPTION_ID_OLD);
    localStorage.removeItem(KEY_SUBSCRIPTION_ID);
    localStorage.removeItem(KEY_LAST_SYNC);
    localStorage.removeItem(KEY_LAST_SESSION);
    localStorage.removeItem(KEY_LAST_WORKER_UPDATE);
    localStorage.removeItem(KEY_LAST_WORKER_VERSION);
    localStorage.removeItem(KEY_DOMAIN);
    localStorage.removeItem(KEY_FACEBOOK_USER_REF);
    localStorage.removeItem(KEY_TOPICS);
    localStorage.removeItem(KEY_TOPICS_VERSION);
    localStorage.removeItem(KEY_TAGS);
    localStorage.removeItem(KEY_ATTRIBUTES);
    localStorage.removeItem(KEY_VISITS);
    localStorage.removeItem(KEY_PIANO_SEGMENTS_HASH);
    sessionStorage.removeItem(KEY_SPEECHBUBBLE_UNREAD_NOTIFICATIONS);
    if (!notManually) {
      localStorage.setItem(KEY_SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS.UNSUBSCRIBED);
      localStorage.setItem(KEY_DENY_TIME, Date.now());
    }

    Object.keys(localStorage).forEach((key) => {
      if ((key && key.startsWith(KEY_TAG_PREFIX)) || key.startsWith(KEY_ATTRIBUTE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });

    const specialLinks = document.querySelectorAll("a[href='#cleverpush-bell-speechbubble']");
    specialLinks.forEach((link) => {
      if (link.classList.contains(KEY_HAS_UNREAD_NOTIFICATIONS)) {
        link.classList.remove(KEY_HAS_UNREAD_NOTIFICATIONS);
      }
    });

    if (this.db) {
      try {
        const transaction = this.db.transaction([INDEXED_DB_TABLE_SUBSCRIPTION], INDEXED_DB_TRANSACTION_READWRITE);
        const subscriptionObjectStore = transaction.objectStore(INDEXED_DB_TABLE_SUBSCRIPTION);
        subscriptionObjectStore.clear();
      } catch (error) {
        log.info('setUnsubscribed db error', error);
      }
    }
  }

  checkIfShouldSync() {
    return new Promise((resolve) => {
      try {
        // sync when subscription id is empty
        if (!this.getSubscriptionId()) {
          resolve(true);
          return;
        }

        let syncHours = SYNC_INTERVAL_HOURS;

        const checkSyncViaInterval = () => {
          const syncInterval = syncHours * MILLIS_PER_HOUR;

          const lastSync = localStorage.getItem(KEY_LAST_SYNC);
          if (!lastSync) {
            resolve(true);
          } else {
            const lastSyncTime = parseInt(lastSync, INT_RADIX);
            if (isNaN(lastSyncTime)) {
              resolve(true);
            } else {
              resolve(lastSyncTime + syncInterval <= Date.now());
            }
          }
        };

        if (this.config.pianoEnabled && this.config.pianoPublicPersistedId) {
          syncHours = SYNC_INTERVAL_HOURS_PIANO;

          try {
            const existingPianoHash = localStorage.getItem(KEY_PIANO_SEGMENTS_HASH);
            getPianoSegmentsHash().then((pianoHash) => {
              localStorage.setItem(KEY_PIANO_SEGMENTS_HASH, pianoHash);
              log.debug('piano segments hashes - existing:', existingPianoHash, 'new:', pianoHash);
              if (pianoHash !== existingPianoHash) {
                resolve(true);
              } else {
                checkSyncViaInterval();
              }
            }).catch((pianoError) => {
              log.debug('piano error', pianoError);
              checkSyncViaInterval();
            });
          } catch (pianoError) {
            log.debug('piano error', pianoError);
            checkSyncViaInterval();
          }
        } else {
          checkSyncViaInterval();
        }
      } catch (error) {
        resolve(true);
      }
    });
  }

  setSynced() {
    localStorage.setItem(KEY_LAST_SYNC, Date.now());
  }

  checkIfShouldUpdateWorker() {
    const currentVersion = typeof VERSION !== 'undefined' ? VERSION : '';
    return new Promise((resolve) => {
      try {
        const lastSync = localStorage.getItem(KEY_LAST_WORKER_UPDATE);
        const lastVersion = localStorage.getItem(KEY_LAST_WORKER_VERSION);
        if (!lastSync) {
          resolve(true);
        } else if (currentVersion) {
          // no time check here, just check if version of sdk updated (is similar to worker version)
          resolve(lastVersion !== currentVersion);
        } else {
          const lastSyncTime = parseInt(lastSync, INT_RADIX);
          if (isNaN(lastSyncTime)) {
            resolve(true);
          } else {
            const syncInterval = UPDATE_WORKER_INTERVAL_24_HOURS * MILLIS_PER_HOUR;
            resolve(lastSyncTime + syncInterval <= Date.now());
          }
        }
      } catch (error) {
        resolve(true);
      }
    });
  }

  setWorkerUpdated() {
    localStorage.setItem(KEY_LAST_WORKER_UPDATE, Date.now());
    localStorage.setItem(KEY_LAST_WORKER_VERSION, typeof VERSION !== 'undefined' ? VERSION : '');
  }

  getVisits() {
    let storedVisits;
    try {
      storedVisits = localStorage.getItem(KEY_VISITS);
    } catch (error) {
      log.info('failed getting visits from storage', error);
    }
    let visits = 0;
    if (storedVisits) {
      visits = parseInt(storedVisits, INT_RADIX);
      if (isNaN(visits)) {
        visits = 0;
      }
    }
    return visits;
  }

  incrementVisits() {
    let visits = this.getVisits();
    visits += 1;
    localStorage.setItem(KEY_VISITS, visits);
  }

  setTopics(topics) {
    log.debug('storageManager.setTopics', topics);

    return new Promise((resolve) => {
      if (topics && topics.length) {
        localStorage.setItem(KEY_TOPICS, JSON.stringify(topics));
      } else if (topics) {
        localStorage.removeItem(KEY_TOPICS);
      }
      resolve(true);
    });
  }

  getTopics() {
    const topicsStr = localStorage.getItem(KEY_TOPICS);
    if (!topicsStr) {
      return Promise.resolve([]);
    }
    try {
      const topics = JSON.parse(topicsStr);
      return Promise.resolve(topics || []);
    } catch (error) {
      return Promise.resolve([]);
    }
  }

  getTagDates() {
    let tagDates = {};
    if (localStorage.getItem(KEY_TAG_DATES)) {
      try {
        tagDates = JSON.parse(localStorage.getItem(KEY_TAG_DATES));
      } catch (error) {
        log.info('failed getting tag dates', error);
      }
    }
    return tagDates;
  }

  getTagDate(id) {
    const date = (this.getTagDates() || {})[id];
    if (date) {
      return new Date(date);
    }
    return null;
  }

  setTags(tags) {
    return new Promise((resolve) => {
      localStorage.setItem(KEY_TAGS, JSON.stringify(tags));

      const tagDates = this.getTagDates() || {};
      tags.forEach((tag) => {
        tagDates[tag] = (new Date()).toISOString();
      });
      localStorage.setItem(KEY_TAG_DATES, JSON.stringify(tagDates));

      resolve(true);
    });
  }

  addTag(tag) {
    return this.getTags().then((tags) => {
      if (tags.indexOf(tag) < 0) {
        tags.push(tag);
      }
      return this.setTags(tags);
    });
  }

  removeTag(tag) {
    return this.getTags().then((tags) => {
      const tagIndex = tags.indexOf(tag);
      if (tagIndex > -1) {
        tags.splice(tagIndex, 1);
        return this.setTags(tags);
      }
      return Promise.resolve();
    });
  }

  getTags() {
    const tagsStr = localStorage.getItem(KEY_TAGS);
    if (!tagsStr) {
      return Promise.resolve([]);
    }
    try {
      const tags = JSON.parse(tagsStr);
      return Promise.resolve(tags || []);
    } catch (error) {
      return Promise.resolve([]);
    }
  }

  /**
   * This function will return array of channel attributes from local storage
   *
   * @returns {Promise<string>}
   */
  getAttributes() {
    const attributesString = localStorage.getItem(KEY_ATTRIBUTES);
    if (!attributesString) {
      return Promise.resolve({});
    }
    try {
      const attributes = JSON.parse(attributesString);
      return Promise.resolve(attributes || {});
    } catch (error) {
      return Promise.resolve({});
    }
  }

  /**
   * This function will return true if subscription has attributes from channel attributes
   *
   * @param {String} attribute Subscription Attribute id
   * @returns {Promise<Boolean>}
   */
  hasAttribute(attribute, value) {
    return this.getAttributes().then((attributes) => Promise.resolve(attributes[attribute] && attributes[attribute] === value));
  }

  hasTag(tag) {
    return this.getTags().then((tags) => Promise.resolve(tags.indexOf(tag) > -1));
  }

  setTempBlocked() {
    sessionStorage.setItem(KEY_SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS.DENIED);
  }

  setNotificationRead(notificationId) {
    try {
      const readNotifications = localStorage.getItem(KEY_READ_NOTIFICATIONS);
      if (readNotifications) {
        const notificationIds = JSON.parse(readNotifications) || [];
        notificationIds.push(notificationId);
        localStorage.setItem(KEY_READ_NOTIFICATIONS, JSON.stringify([...new Set(notificationIds)]));
      } else {
        const notificationIds = [];
        notificationIds.push(notificationId);
        localStorage.setItem(KEY_READ_NOTIFICATIONS, JSON.stringify(notificationIds));
      }
    } catch (error) {
      log.info('failed to set reads notifications', error);
    }
  }

  isNotificationRead(notificationId) {
    try {
      const readNotifications = localStorage.getItem(KEY_READ_NOTIFICATIONS);
      if (readNotifications) {
        const notificationIds = JSON.parse(readNotifications) || [];
        return notificationIds.includes(notificationId);
      }
      return false;
    } catch (error) {
      log.info('failed to getting notifications', error);
      return false;
    }
  }

  checkUnreadNotifications(notifications) {
    try {
      const readNotifications = localStorage.getItem(KEY_READ_NOTIFICATIONS);
      const notificationIds = JSON.parse(readNotifications || '[]') || [];
      const checkUnread = notifications.some((item) => !notificationIds.includes(item._id));
      const count = notifications.filter((item) => !notificationIds.includes(item._id)).length;
      localStorage.setItem(KEY_UNREAD_NOTIFICATIONS_COUNT, count);
      return checkUnread;
    } catch (err) {
      log.info('failed to check unread notifications');
      return false;
    }
  }

  deleteOldNotificationsIds(notifications) {
    try {
      const readNotifications = localStorage.getItem(KEY_READ_NOTIFICATIONS);
      if (readNotifications) {
        const notificationIds = JSON.parse(readNotifications) || [];
        const newNotificationsIds = notificationIds.filter((item) => notifications.some((notification) => notification._id === item));
        localStorage.setItem(KEY_READ_NOTIFICATIONS, JSON.stringify([...new Set(newNotificationsIds)]));
      }
    } catch (error) {
      log.info('failed to delete notifications', error);
    }
  }
}
